/**
 * Shared helpers for the preflight "operational columns" taxonomy.
 *
 * Both the Segmentation "Add filter" dropdown and the Identity "Map to question"
 * source picker hide columns that preflight flagged as operational noise
 * (e.g. "Captured variable …"). Keeping this logic in one place guarantees
 * the two dropdowns behave identically.
 */

/** Normalize a raw `operationalColumns` list into a lookup set. */
export function toOperationalSet(
  names: readonly string[] | null | undefined,
): Set<string> {
  if (!Array.isArray(names) || names.length === 0) {
    return new Set<string>()
  }
  const out = new Set<string>()
  for (const raw of names) {
    if (typeof raw !== 'string') continue
    const trimmed = raw.trim()
    if (trimmed.length > 0) out.add(trimmed)
  }
  return out
}

/**
 * Drop entries whose `sourceColumn` is in `opSet`. Empty/missing opSet is a no-op
 * so callers can apply this unconditionally without regressing preflight-less flows.
 *
 * Entries whose `sourceColumn` is in `pinnedColumns` are never dropped — use this
 * to keep a user's current/selected column visible even if it got re-flagged as
 * operational after the fact.
 */
export function filterCleanColumns<T extends { sourceColumn: string }>(
  items: readonly T[],
  opSet: Set<string>,
  pinnedColumns?: ReadonlySet<string> | null,
): T[] {
  if (opSet.size === 0) {
    return items.slice()
  }
  const pinned = pinnedColumns ?? null
  return items.filter(
    (item) =>
      (pinned != null && pinned.has(item.sourceColumn)) ||
      !opSet.has(item.sourceColumn),
  )
}
